import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { map, switchMap } from 'rxjs';
import { FileDropComponent, FileUploadServiceV2, SvgIconsComponent } from '@zayan-erp/shared';
import { SalesApsService } from '../../../services/sales-aps.service';
import { WelcomeEmailPreview } from '../../../models/email-templates.models';

const ACCEPTED_FORMATS = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

interface PendingUpload {
  key: number;
  file: File;
  status: 'uploading' | 'done' | 'failed';
  s3DocumentId?: string;
}

@Component({
  selector: 'lib-welcome-email-send-dialog',
  imports: [FileDropComponent, SvgIconsComponent],
  templateUrl: './welcome-email-send-dialog.component.html',
  styleUrl: './welcome-email-send-dialog.component.scss'
})
export class WelcomeEmailSendDialogComponent implements OnInit {
  /** Set by the opener through NgbModalRef.componentInstance, before the first change detection. */
  dealId = '';

  readonly acceptedFormats = ACCEPTED_FORMATS;
  #activeModal = inject(NgbActiveModal);
  #salesApsService = inject(SalesApsService);
  #uploadService = inject(FileUploadServiceV2);
  #sanitizer = inject(DomSanitizer);
  #nextKey = 0;

  preview = signal<WelcomeEmailPreview | null>(null);
  loading = signal(true);
  loadError = signal<string | null>(null);
  attachAps = signal(true);
  uploads = signal<PendingUpload[]>([]);
  fileError = signal<string | null>(null);
  sending = signal(false);

  previewUrl = computed<SafeResourceUrl | null>(() => {
    const html = this.preview()?.html;
    return html
      ? this.#sanitizer.bypassSecurityTrustResourceUrl('data:text/html;charset=utf-8,' + encodeURIComponent(html))
      : null;
  });
  apsAvailable = computed(() => !!this.preview()?.aps?.available);
  canSend = computed(
    () => !!this.preview() && !this.sending() && this.uploads().every(upload => upload.status === 'done')
  );

  ngOnInit(): void {
    this.#salesApsService.getWelcomeEmailPreview(this.dealId).subscribe({
      next: preview => {
        this.preview.set(preview);
        this.attachAps.set(!!preview?.aps?.available);
        this.loading.set(false);
      },
      error: error => {
        this.loadError.set(error?.error?.message ?? 'The welcome email preview could not be loaded.');
        this.loading.set(false);
      }
    });
  }

  onFilesDropped(entries: NgxFileDropEntry[]): void {
    this.fileError.set(null);
    for (const entry of entries) {
      if (entry.fileEntry.isFile) {
        (entry.fileEntry as FileSystemFileEntry).file(file => this.#addFile(file));
      }
    }
  }

  startUpload(key: number): void {
    const upload = this.uploads().find(item => item.key === key);
    if (!upload) return;

    this.#patchUpload(key, { status: 'uploading' });
    let finished = false;
    this.#uploadService
      .getPrivateAWSSignedUrl(upload.file)
      .pipe(switchMap(signed => this.#uploadService.uploadFileToS3WithFetch(signed).pipe(map(() => signed.documentId))))
      .subscribe({
        next: documentId => {
          finished = true;
          this.#patchUpload(key, { status: 'done', s3DocumentId: documentId });
        },
        error: () => {
          finished = true;
          this.#patchUpload(key, { status: 'failed' });
        },
        // The upload service swallows its own errors and completes empty, so no value is a failure too.
        complete: () => {
          if (!finished) this.#patchUpload(key, { status: 'failed' });
        }
      });
  }

  removeUpload(key: number): void {
    this.uploads.update(list => list.filter(upload => upload.key !== key));
  }

  toggleAttachAps(event: Event): void {
    this.attachAps.set((event.target as HTMLInputElement).checked);
  }

  send(): void {
    if (!this.canSend()) return;

    this.sending.set(true);
    this.#salesApsService
      .sendWelcomeEmail(this.dealId, false, {
        attachAps: this.apsAvailable() && this.attachAps(),
        customDocuments: this.uploads().map(upload => ({
          s3DocumentId: upload.s3DocumentId as string,
          fileName: upload.file.name
        }))
      })
      .subscribe({
        next: () => this.#activeModal.close(true),
        error: () => this.sending.set(false)
      });
  }

  cancel(): void {
    this.#activeModal.dismiss();
  }

  formatSize(bytes: number): string {
    return bytes >= 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  #addFile(file: File): void {
    const extension = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
    if (!ACCEPTED_FORMATS.split(',').includes(extension)) {
      this.fileError.set(`${file.name}: only PDF, Word, JPG or PNG files can be attached.`);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.fileError.set(`${file.name} is larger than 10 MB.`);
      return;
    }
    if (this.uploads().length >= MAX_FILES) {
      this.fileError.set('At most 10 documents can be attached.');
      return;
    }

    const key = this.#nextKey++;
    this.uploads.update(list => [...list, { key, file, status: 'uploading' }]);
    this.startUpload(key);
  }

  #patchUpload(key: number, patch: Partial<PendingUpload>): void {
    this.uploads.update(list => list.map(upload => (upload.key === key ? { ...upload, ...patch } : upload)));
  }
}
